import { listPublicPlans } from '@wix/ambassador-pricing-plans-v2-plan/http';
import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { queryPlans } from '@wix/ambassador-pricing-plans-v3-plan/http';
import { EndType, Period, Plan } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { plansPageView } from '@wix/bi-logger-pricing-plans-data/v2';
import { PlanWidgetRole, SinglePlanWidgetRole } from '@wix/pricing-plans-common/blocks';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { EditorScriptFlowAPI } from '@wix/yoshi-flow-editor';
import { Analytics } from '../../services/analytics';
import { WarmupData } from '../../services/WarmupData';
import { SinglePlanInteractions } from '../../types/SinglePlanFedops';
import { BiCheckoutVersion, isViewerOnEcom } from '../../utils/ecom';
import { retry401 } from '../../utils/retry401';
import { captureViewerException } from '../../utils/viewer-errors';
import { CtaClickHandler } from '../Plan/viewer.controller';
import model from './model';

const getDemoPlanV3 = (t: EditorScriptFlowAPI['translations']['t']): Plan => ({
  name: t('blocks.demo-plan.name'),
  description: t('blocks.demo-plan.description'),
  currency: 'USD',
  pricingVariants: [
    {
      pricingStrategies: [{ flatRate: { amount: '15' } }],
      billingTerms: {
        billingCycle: { count: '3', period: Period.MONTH },
        cyclesCompletedDetails: { billingCycleCount: '3' },
        endType: EndType.CYCLES_COMPLETED,
      },
    },
  ],
  perks: [
    { description: t('blocks.demo-plan.perk') },
    { description: t('blocks.demo-plan.perk') },
    { description: t('blocks.demo-plan.perk') },
  ],
});

export default model.createController(({ $w, $widget, flowAPI, controllerConfig }) => {
  let autoLoadPlan: boolean = true;
  const isV3Enabled = flowAPI.experiments.enabled(TPA_EXPERIMENTS.PLAN_V3_MIGRATION);

  const warmUpData = new WarmupData(controllerConfig.compId, controllerConfig.wixCodeApi, flowAPI);

  const getDemoPlanIfEditor = (): Plan | null => {
    if (!flowAPI.environment.isEditor) {
      return null;
    }
    return getDemoPlanV3(flowAPI.translations.t);
  };

  const fetchPlan = async (planId?: string): Promise<PublicPlan | Plan | null> => {
    if (!planId) {
      return getDemoPlanIfEditor();
    }

    const cacheKey = `single-plan-data-${planId}`;

    if (isV3Enabled) {
      const getV3Plan = () =>
        flowAPI.errorHandler.withErrorHandler(
          () => flowAPI.httpClient.request(queryPlans({ query: { filter: { id: planId, archived: false } } })),
          { errorCodesMap: { applicationError: { PRICING_PLANS_NOT_INSTALLED: false } } },
        );
      const v3Response = await warmUpData.cache(cacheKey, () => getV3Plan());
      return v3Response.data.plans?.[0] ?? getDemoPlanIfEditor();
    }

    const getV2Plan = () =>
      flowAPI.errorHandler.withErrorHandler(() => flowAPI.httpClient.request(listPublicPlans({ planIds: [planId] })), {
        errorCodesMap: { applicationError: { PRICING_PLANS_NOT_INSTALLED: false } },
      });

    const v2Response = await warmUpData.cache(cacheKey, () => getV2Plan());
    return v2Response.data.plans?.[0] ?? getDemoPlanIfEditor();
  };

  const setPlan = async (planId: string) => {
    flowAPI.panoramaClient?.transaction(SinglePlanInteractions.SetPlanData).start();
    if (!flowAPI.environment.isEditor && !flowAPI.environment.isSSR) {
      showSpinner();
    }

    try {
      const plan = await retry401(flowAPI.controllerConfig.wixCodeApi, flowAPI, () => fetchPlan(planId));
      if (!plan) {
        showEmptyState();
      } else {
        const isOnEcom = await isViewerOnEcom(
          flowAPI.httpClient,
          controllerConfig.wixCodeApi,
          controllerConfig.appParams,
          flowAPI.errorHandler,
        );

        $w(`#${PlanWidgetRole.PlanWidget}`).setPlan(plan);
        await showLoadedState();
        await $w(`#${PlanWidgetRole.PlanWidget}`).registerCtaHandler(plan, 'single', isOnEcom);
        const analytics = new Analytics(flowAPI.controllerConfig.wixCodeApi.window);
        analytics.addProductImpression({ visiblePlans: [plan] });
        reportWidgetLoaded(isOnEcom);
      }
      flowAPI.panoramaClient?.transaction(SinglePlanInteractions.SetPlanData).finish();
    } catch (e) {
      const error = flowAPI.errorHandler.getResolvedError(e);
      showEmptyState(error.message);
      captureViewerException(flowAPI, e);
    }
  };

  const showSpinner = () => {
    return $w(`#${SinglePlanWidgetRole.MultiStateBox}`).changeState(SinglePlanWidgetRole.StateSpinner);
  };

  const showEmptyState = (text?: string) => {
    $w(`#${PlanWidgetRole.EmptyStateTitle}`).text = flowAPI.translations.t('blocks.single-plan.empty.title');
    $w(`#${PlanWidgetRole.EmptyStateText}`).text = text ?? flowAPI.translations.t('blocks.single-plan.empty.text');
    return $w(`#${SinglePlanWidgetRole.MultiStateBox}`).changeState(SinglePlanWidgetRole.StateEmpty);
  };

  const showLoadedState = () => {
    return $w(`#${SinglePlanWidgetRole.MultiStateBox}`).changeState(SinglePlanWidgetRole.StateLoaded);
  };

  const getIsInitialLoadWarmupKey = (planId: string) => `single-plan-initial-load-${planId}`;

  const setIsInitialLoad = (planId: string) => {
    const key = getIsInitialLoadWarmupKey(planId);
    warmUpData.set(key, true);
  };

  const getIsInitialLoad = (planId: string) => {
    const key = getIsInitialLoadWarmupKey(planId);
    return Boolean(warmUpData.get(key));
  };

  const reportWidgetLoaded = async (isEcom: boolean) => {
    flowAPI.bi?.report(
      plansPageView({
        widgetType: 'single_blocks',
        checkoutVersion: isEcom ? BiCheckoutVersion.Ecom : BiCheckoutVersion.PricingPlans,
      }),
    );
  };

  $widget.onPropsChanged(async (oldProps, newProps) => {
    try {
      const plan = await fetchPlan(newProps.planId);
      $w(`#${PlanWidgetRole.PlanWidget}`).setPlan(plan);
      $w(`#${PlanWidgetRole.PlanWidget}`).registerCtaHandler(plan, 'single');
    } catch (e) {
      const error = flowAPI.errorHandler.getResolvedError(e);
      showEmptyState(error.message);
      captureViewerException(flowAPI, e);
    }
  });

  return {
    pageReady: async () => {
      if (!autoLoadPlan) {
        return;
      }
      // Viewer controller can be executed without widget being rendered
      // This happens in editor, when page has an OOI widget, but not blocks widget
      const isWidgetRendered = $w(`#${PlanWidgetRole.PlanWidget}`).setPlan !== undefined;
      if (isWidgetRendered) {
        const planId = $widget.props.planId;
        if (flowAPI.environment.isSSR) {
          setIsInitialLoad(planId);
          await setPlan(planId);
        } else if (getIsInitialLoad(planId)) {
          await setPlan(planId);
        } else {
          // Do not await for setPlan if page was visited by navigation and not by directly visiting the site,
          // so pageReady resolves faster and doesn't slow down the navigation
          setPlan($widget.props.planId);
        }
      } else {
        console.error('Single Plan Widget does not exist in page');
      }
    },
    exports: {
      onSelect: (cb: CtaClickHandler) => {
        $w(`#${PlanWidgetRole.PlanWidget}`).onSelect(cb);
      },
      setPlan: (planId: string) => {
        autoLoadPlan = false;
        setPlan(planId);
      },
    },
  };
});
